import React, { useEffect, useState } from "react";
import AdminHeader from "./Components/AdminHeader";
import AdminSidebar from "./Components/AdminSidebar";
import BlogCategoryModal from "./Components/BlogCategoryModal";
import BlogModal from "./Components/BlogModal";
import api from "../../api";
import { IMAGE_URL } from "../../path";
import {
    BlogCategory,
    Blog,
} from "../../Blog";
import "../../styles/Login.scss";

const AdminBlogs: React.FC = () => {
    const [categories, setCategories] = useState<BlogCategory[]>([]);
    const [blogs, setBlogs] = useState<Blog[]>([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [selectedCategory, setSelectedCategory] =
        useState<BlogCategory | null>(null);

    const [selectedBlog, setSelectedBlog] =
        useState<Blog | null>(null);

    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [showBlogModal, setShowBlogModal] = useState(false);

    const [deletingCategoryId, setDeletingCategoryId] =
        useState<number | null>(null);

    const [deletingBlogId, setDeletingBlogId] =
        useState<number | null>(null);

    const [movingCategoryId, setMovingCategoryId] =
        useState<number | null>(null);

    const [movingBlogId, setMovingBlogId] =
        useState<number | null>(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            setError("");

            const [
                categoriesResponse,
                blogsResponse,
            ] = await Promise.all([
                api.get("/admin/blog-categories"),
                api.get("/admin/blogs"),
            ]);

            const loadedCategories =
                categoriesResponse.data.data ??
                categoriesResponse.data;

            const loadedBlogs =
                blogsResponse.data.data ??
                blogsResponse.data;

            setCategories(
                [...loadedCategories].sort(
                    (a: BlogCategory, b: BlogCategory) =>
                        a.display_order - b.display_order ||
                        a.id - b.id
                )
            );

            setBlogs(
                [...loadedBlogs].sort(
                    (a: Blog, b: Blog) =>
                        a.display_order - b.display_order ||
                        a.id - b.id
                )
            );
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to load blogs."
            );
        } finally {
            setLoading(false);
        }
    };

    /*
     * =========================================================
     * CATEGORY MODAL
     * =========================================================
     */

    const handleAddCategory = () => {
        setSelectedCategory(null);
        setShowCategoryModal(true);
        setError("");
        setSuccess("");
    };

    const handleEditCategory = (category: BlogCategory) => {
        setSelectedCategory(category);
        setShowCategoryModal(true);
        setError("");
        setSuccess("");
    };

    const handleCloseCategoryModal = () => {
        setShowCategoryModal(false);
        setSelectedCategory(null);
    };

    const handleCategorySaved = async () => {
        handleCloseCategoryModal();

        setSuccess("Blog category saved successfully.");

        await loadData();
    };

    /*
     * =========================================================
     * BLOG MODAL
     * =========================================================
     */

    const handleAddBlog = () => {
        setSelectedBlog(null);
        setShowBlogModal(true);
        setError("");
        setSuccess("");
    };

    const handleEditBlog = (blog: Blog) => {
        setSelectedBlog(blog);
        setShowBlogModal(true);
        setError("");
        setSuccess("");
    };

    const handleCloseBlogModal = () => {
        setShowBlogModal(false);
        setSelectedBlog(null);
    };

    const handleBlogSaved = async () => {
        handleCloseBlogModal();

        setSuccess("Blog saved successfully.");

        await loadData();
    };

    /*
     * =========================================================
     * CATEGORY DELETE
     * =========================================================
     */

    const handleDeleteCategory = async (
        category: BlogCategory
    ) => {
        if (category.blogs_count > 0) {
            setError(
                "This blog category cannot be deleted because it still contains blogs."
            );
            setSuccess("");
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete "${category.name}"?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingCategoryId(category.id);
            setError("");
            setSuccess("");

            await api.delete(
                `/admin/blog-categories/${category.id}`
            );

            setSuccess(
                "Blog category deleted successfully."
            );

            await loadData();
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to delete blog category."
            );
        } finally {
            setDeletingCategoryId(null);
        }
    };

    /*
     * =========================================================
     * BLOG DELETE
     * =========================================================
     */

    const handleDeleteBlog = async (blog: Blog) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${blog.title}"?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingBlogId(blog.id);
            setError("");
            setSuccess("");

            await api.delete(`/admin/blogs/${blog.id}`);

            setSuccess("Blog deleted successfully.");

            await loadData();
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to delete blog."
            );
        } finally {
            setDeletingBlogId(null);
        }
    };

    /*
     * =========================================================
     * MOVE CATEGORY
     * =========================================================
     */

    const handleMoveCategory = async (
        category: BlogCategory,
        direction: "up" | "down"
    ) => {
        try {
            setMovingCategoryId(category.id);
            setError("");
            setSuccess("");

            await api.post(
                `/admin/blog-categories/${category.id}/move`,
                {
                    direction,
                }
            );

            await loadData();
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to move blog category."
            );
        } finally {
            setMovingCategoryId(null);
        }
    };

    /*
     * =========================================================
     * MOVE BLOG
     * =========================================================
     */

    const handleMoveBlog = async (
        blog: Blog,
        direction: "up" | "down"
    ) => {
        try {
            setMovingBlogId(blog.id);
            setError("");
            setSuccess("");

            await api.post(
                `/admin/blogs/${blog.id}/move`,
                {
                    direction,
                }
            );

            await loadData();
        } catch (err: any) {
            console.error(err);

            setError(
                err?.response?.data?.message ||
                "Failed to move blog."
            );
        } finally {
            setMovingBlogId(null);
        }
    };

    /*
     * =========================================================
     * CATEGORY BLOGS
     * =========================================================
     */

    const getBlogsForCategory = (categoryId: number) => {
        return blogs
            .filter(
                (blog) =>
                    blog.blog_category_id === categoryId
            )
            .sort(
                (a, b) =>
                    a.display_order - b.display_order ||
                    a.id - b.id
            );
    };

    /*
     * =========================================================
     * CATEGORY MOVE STATE
     * =========================================================
     */

    const canMoveCategoryUp = (index: number) => {
        return index > 0;
    };

    const canMoveCategoryDown = (index: number) => {
        return index < categories.length - 1;
    };

    /*
     * =========================================================
     * BLOG MOVE STATE
     * =========================================================
     */

    const canMoveBlogUp = (
        blog: Blog,
        categoryBlogs: Blog[]
    ) => {
        const index = categoryBlogs.findIndex(
            (item) => item.id === blog.id
        );

        return index > 0;
    };

    const canMoveBlogDown = (
        blog: Blog,
        categoryBlogs: Blog[]
    ) => {
        const index = categoryBlogs.findIndex(
            (item) => item.id === blog.id
        );

        return index < categoryBlogs.length - 1;
    };

    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (
        <div className="admin-page">
            <AdminHeader />

            <div className="admin-body">
                <AdminSidebar />

                <main className="admin-content">
                    <div className="admin-users">

                        {/* =================================================
                            PAGE HEADER
                        ================================================== */}

                        <div className="admin-page-header">
                            <div>
                                <h1>Blogs</h1>
                                <p>
                                    Manage blog categories and blogs.
                                </p>
                            </div>

                            <div className="admin-user-actions">
                                <button
                                    type="button"
                                    className="admin-primary-button"
                                    onClick={handleAddCategory}
                                >
                                    Add Category
                                </button>

                                <button
                                    type="button"
                                    className="admin-primary-button"
                                    onClick={handleAddBlog}
                                >
                                    Add Blog
                                </button>
                            </div>
                        </div>

                        {/* =================================================
                            MESSAGES
                        ================================================== */}

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="form-success">
                                {success}
                            </div>
                        )}

                        {loading ? (
                            <p>Loading blogs...</p>
                        ) : (
                            <>
                                {/* =============================================
                                    BLOG CATEGORIES
                                ============================================== */}

                                <section className="admin-content-section">
                                    <div className="admin-page-header">
                                        <div>
                                            <h2>Blog Categories</h2>
                                            <p>
                                                Manage the categories used to
                                                organise blogs.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={handleAddCategory}
                                        >
                                            Add Category
                                        </button>
                                    </div>

                                    {categories.length === 0 ? (
                                        <p>
                                            No blog categories found.
                                        </p>
                                    ) : (
                                        <div className="admin-users-table">
                                            <table>
                                                <thead>
                                                <tr>
                                                    <th>Icon</th>
                                                    <th>Category</th>
                                                    <th>Blogs</th>
                                                    <th>Active</th>
                                                    <th>Actions</th>
                                                </tr>
                                                </thead>

                                                <tbody>
                                                {categories.map(
                                                    (
                                                        category,
                                                        index
                                                    ) => (
                                                        <tr
                                                            key={
                                                                category.id
                                                            }
                                                        >
                                                            <td>
                                                                {category.icon ? (
                                                                    <img
                                                                        src={`${IMAGE_URL}/${category.icon}`}
                                                                        alt={`${category.name} icon`}
                                                                        className="admin-svg-preview"
                                                                    />
                                                                ) : (
                                                                    <span>
                                                                            —
                                                                        </span>
                                                                )}
                                                            </td>

                                                            <td>
                                                                <strong>
                                                                    {
                                                                        category.name
                                                                    }
                                                                </strong>
                                                            </td>

                                                            <td>
                                                                {
                                                                    category.blogs_count
                                                                }
                                                            </td>

                                                            <td>
                                                                    <span
                                                                        className={`admin-role ${
                                                                            category.active
                                                                                ? "admin-role-admin"
                                                                                : "admin-role-editor"
                                                                        }`}
                                                                    >
                                                                        {category.active
                                                                            ? "Active"
                                                                            : "Inactive"}
                                                                    </span>
                                                            </td>

                                                            <td>
                                                                <div className="admin-user-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button"
                                                                        disabled={
                                                                            movingCategoryId ===
                                                                            category.id ||
                                                                            !canMoveCategoryUp(
                                                                                index
                                                                            )
                                                                        }
                                                                        onClick={() =>
                                                                            handleMoveCategory(
                                                                                category,
                                                                                "up"
                                                                            )
                                                                        }
                                                                    >
                                                                        ↑
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button"
                                                                        disabled={
                                                                            movingCategoryId ===
                                                                            category.id ||
                                                                            !canMoveCategoryDown(
                                                                                index
                                                                            )
                                                                        }
                                                                        onClick={() =>
                                                                            handleMoveCategory(
                                                                                category,
                                                                                "down"
                                                                            )
                                                                        }
                                                                    >
                                                                        ↓
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button"
                                                                        onClick={() =>
                                                                            handleEditCategory(
                                                                                category
                                                                            )
                                                                        }
                                                                    >
                                                                        Edit
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button admin-table-button-danger"
                                                                        disabled={
                                                                            deletingCategoryId ===
                                                                            category.id
                                                                        }
                                                                        onClick={() =>
                                                                            handleDeleteCategory(
                                                                                category
                                                                            )
                                                                        }
                                                                    >
                                                                        {deletingCategoryId ===
                                                                        category.id
                                                                            ? "Deleting..."
                                                                            : "Delete"}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </section>

                                {/* =============================================
                                    BLOGS
                                ============================================== */}

                                <section className="admin-content-section">
                                    <div className="admin-page-header">
                                        <div>
                                            <h2>Blogs</h2>
                                            <p>
                                                Manage blogs within each
                                                category.
                                            </p>
                                        </div>

                                        <button
                                            type="button"
                                            className="admin-primary-button"
                                            onClick={handleAddBlog}
                                        >
                                            Add Blog
                                        </button>
                                    </div>

                                    {categories.length === 0 ? (
                                        <p>
                                            Create a blog category before
                                            adding blogs.
                                        </p>
                                    ) : (
                                        categories.map((category) => {
                                            const categoryBlogs =
                                                getBlogsForCategory(
                                                    category.id
                                                );

                                            return (
                                                <div
                                                    key={category.id}
                                                    className="admin-content-section"
                                                >
                                                    <div className="admin-page-header">
                                                        <div>
                                                            <h3>
                                                                {
                                                                    category.name
                                                                }
                                                            </h3>

                                                            <p>
                                                                {
                                                                    categoryBlogs.length
                                                                }{" "}
                                                                blog
                                                                {categoryBlogs.length !==
                                                                1
                                                                    ? "s"
                                                                    : ""}
                                                            </p>
                                                        </div>
                                                    </div>

                                                    {categoryBlogs.length ===
                                                    0 ? (
                                                        <p>
                                                            No blogs in this
                                                            category.
                                                        </p>
                                                    ) : (
                                                        <div className="admin-users-table">
                                                            <table>
                                                                <thead>
                                                                <tr>
                                                                    <th>
                                                                        Image
                                                                    </th>
                                                                    <th>
                                                                        Blog
                                                                    </th>
                                                                    <th>
                                                                        Read Time
                                                                    </th>
                                                                    <th>
                                                                        Active
                                                                    </th>
                                                                    <th>
                                                                        Actions
                                                                    </th>
                                                                </tr>
                                                                </thead>

                                                                <tbody>
                                                                {categoryBlogs.map(
                                                                    (
                                                                        blog
                                                                    ) => (
                                                                        <tr
                                                                            key={
                                                                                blog.id
                                                                            }
                                                                        >
                                                                            <td>
                                                                                {blog.preview_image ? (
                                                                                    <img
                                                                                        src={`${IMAGE_URL}/${blog.preview_image}`}
                                                                                        alt={
                                                                                            blog.title
                                                                                        }
                                                                                        style={{
                                                                                            width: "80px",
                                                                                            height: "60px",
                                                                                            objectFit:
                                                                                                "cover",
                                                                                            borderRadius:
                                                                                                "6px",
                                                                                        }}
                                                                                    />
                                                                                ) : (
                                                                                    <span>
                                                                                            —
                                                                                        </span>
                                                                                )}
                                                                            </td>

                                                                            <td>
                                                                                {
                                                                                    blog.title
                                                                                }
                                                                            </td>

                                                                            <td>
                                                                                {
                                                                                    blog.read_time
                                                                                }{" "}
                                                                                min
                                                                            </td>

                                                                            <td>
                                                                                    <span
                                                                                        className={`admin-role ${
                                                                                            blog.active
                                                                                                ? "admin-role-admin"
                                                                                                : "admin-role-editor"
                                                                                        }`}
                                                                                    >
                                                                                        {blog.active
                                                                                            ? "Active"
                                                                                            : "Inactive"}
                                                                                    </span>
                                                                            </td>

                                                                            <td>
                                                                                <div className="admin-user-actions">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="admin-table-button"
                                                                                        disabled={
                                                                                            movingBlogId ===
                                                                                            blog.id ||
                                                                                            !canMoveBlogUp(
                                                                                                blog,
                                                                                                categoryBlogs
                                                                                            )
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleMoveBlog(
                                                                                                blog,
                                                                                                "up"
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        ↑
                                                                                    </button>

                                                                                    <button
                                                                                        type="button"
                                                                                        className="admin-table-button"
                                                                                        disabled={
                                                                                            movingBlogId ===
                                                                                            blog.id ||
                                                                                            !canMoveBlogDown(
                                                                                                blog,
                                                                                                categoryBlogs
                                                                                            )
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleMoveBlog(
                                                                                                blog,
                                                                                                "down"
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        ↓
                                                                                    </button>

                                                                                    <button
                                                                                        type="button"
                                                                                        className="admin-table-button"
                                                                                        onClick={() =>
                                                                                            handleEditBlog(
                                                                                                blog
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        Edit
                                                                                    </button>

                                                                                    <button
                                                                                        type="button"
                                                                                        className="admin-table-button admin-table-button-danger"
                                                                                        disabled={
                                                                                            deletingBlogId ===
                                                                                            blog.id
                                                                                        }
                                                                                        onClick={() =>
                                                                                            handleDeleteBlog(
                                                                                                blog
                                                                                            )
                                                                                        }
                                                                                    >
                                                                                        {deletingBlogId ===
                                                                                        blog.id
                                                                                            ? "Deleting..."
                                                                                            : "Delete"}
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })
                                    )}
                                </section>
                            </>
                        )}
                    </div>
                </main>
            </div>

            {/* =========================================================
                CATEGORY MODAL
            ========================================================= */}

            {showCategoryModal && (
                <BlogCategoryModal
                    category={selectedCategory}
                    onClose={handleCloseCategoryModal}
                    onSaved={handleCategorySaved}
                />
            )}

            {/* =========================================================
                BLOG MODAL
            ========================================================= */}

            {showBlogModal && (
                <BlogModal
                    blog={selectedBlog}
                    categories={categories}
                    onClose={handleCloseBlogModal}
                    onSaved={handleBlogSaved}
                />
            )}
        </div>
    );
};

export default AdminBlogs;
